<script setup>
import { computed, toRefs, unref } from "vue";
import { useStore } from "vuex";
import AcademicCredits from "@/components/shared/Card/AcademicCredits.vue";
import ActionButtonsCard from "@/components/shared/Card/ActionButtonsCard.vue";
import ProgramPriceCard from "@/components/shared/Card/ProgramPriceCard.vue";
import TitleCard from "@/components/shared/Card/TitleCard.vue";
import { hasDisplayStudentDiscountEnabled } from "@/composables/authorization";
import { API_ENTITY_INFORMATION } from "@/constants.ts";
import { hidePrice } from "@/util/pricingLabel";
import { handleClick, onMouseUp, useApplicationProgress } from "../../composables.js";
import HomeSessionCardContent from "../HomeSessionCardContent";
import {
  cardImage,
  getAcademicCredits,
  getIsPastAppDeadline,
  getPriceInDollars,
  getRedirectUrl,
  getSessionCardTitle,
} from "../utils";

defineOptions({
  name: "HomeSessionCard",
});

defineEmits(["bookmarkClicked", "viewClicked", "applyClicked"]);

const store = useStore();

const billingRules = computed(() => store.state.billingRules);
// Used by the flag-off `priceInDollars` path; kept alongside the new discount computeds for graceful fallback.
const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");

const props = defineProps({
  program: {
    type: Object,
    default() {
      return {};
    },
  },
  eventCategory: {
    type: String,
    default: "",
  },
  imageUrl: {
    type: String,
    default: "",
  },
  addedClass: {
    type: String,
    default: "",
  },
  domainEntityId: {
    type: String,
    required: true,
  },
});

const { getRegisterType, isReady } = useApplicationProgress();
const { program, domainEntityId } = toRefs(props);

const session = computed(() => unref(program)?.session);
const sessionTypes = computed(() => unref(session)?.session_types);

const program_page = computed(() => unref(program)?.program_page);
const programPageId = computed(() => unref(program_page)?.id);

const showPriceComponent = computed(() => !hidePrice(unref(session), unref(billingRules)));
const sessionCardTitle = computed(() => getSessionCardTitle(unref(session)));
// Flag-off price label — preserves the pre-DATSC-889 behavior.
const priceInDollars = computed(() => getPriceInDollars(unref(program), unref(homeInstitutionId)));
const academicCredits = computed(() => getAcademicCredits(unref(session)));
const redirectUrl = computed(() => getRedirectUrl(unref(program)));
const isPastAppDeadline = computed(() => getIsPastAppDeadline(unref(program)));
const showSessionTypes = computed(() => unref(sessionTypes)?.length > 1);

const originalCost = computed(() => {
  const prices = session.value?.prices;
  const retVal = prices?.base;
  return homeInstitutionId.value && prices && Object.hasOwn(prices, homeInstitutionId.value)
    ? prices[homeInstitutionId.value]
    : retVal;
});

const actualCost = computed(() => {
  const prices = session.value?.prices;
  const base = prices?.base;
  if (
    domainEntityId.value &&
    domainEntityId.value !== API_ENTITY_INFORMATION.id &&
    prices &&
    Object.hasOwn(prices, domainEntityId.value)
  ) {
    return prices[domainEntityId.value];
  }
  if (homeInstitutionId.value && prices && Object.hasOwn(prices, homeInstitutionId.value)) {
    return prices[homeInstitutionId.value];
  }
  return base;
});
const discountTitle = computed(() => session.value?.titles?.[domainEntityId.value]);
</script>

<template>
  <div
    :data-testid="`session-${program?.session?.id}-program-card`"
    class="program-card-base flex flex-col lg:min-h-[18.75rem] max-w-6xl lg:gap-x-4 cursor-pointer"
    :class="addedClass"
    @mouseup="onMouseUp"
    @click="handleClick($emit)"
  >
    <TitleCard
      :title="sessionCardTitle"
      :action-id="programPageId"
      :show-bookmark="false"
      @bookmark-clicked="(id) => $emit('bookmarkClicked', id)"
    />
    <div class="min-h-125p">
      <div class="h-32 relative overflow-hidden">
        <img
          :data-testid="`card-image-${programPageId}`"
          :src="cardImage(imageUrl, program)"
          :alt="program_page?.name || ''"
          class="absolute inset-0 h-full w-full object-cover bg-gray-75"
        />
      </div>
    </div>

    <div
      class="px-4 lg:grid lg:gap-x-4 flex flex-col flex-1 lg:col-span-1 lg:grid-cols-1"
    >
      <HomeSessionCardContent :program="program" />
      <div class="flex flex-col justify-end gap-y-11">
        <div data-testid="api-card-description">
          <ProgramPriceCard
            v-if="showPriceComponent && hasDisplayStudentDiscountEnabled"
            :original-cost="originalCost"
            :discount-title="discountTitle"
            :actual-cost="actualCost"
          />
          <ProgramPriceCard
            v-else-if="showPriceComponent"
            :price-in-dollars="priceInDollars"
          />
          <AcademicCredits
            v-if="academicCredits"
            :academic-credits="academicCredits"
          />
        </div>
        <div
          class="flex flex-col sm:flex-row sm:justify-around justify-center items-center self-end w-full gap-4 mb-6"
        >
          <ActionButtonsCard
            v-if="isReady"
            :show-view-link="true"
            :redirect-url="redirectUrl"
            :program="program"
            custom-apply-class="program-card-apply-button-base"
            :is-past-app-deadline="isPastAppDeadline"
            :register-type="getRegisterType(session)"
            :event-category="eventCategory"
            @apply-clicked="() => $emit('applyClicked')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
